import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { AjsResponse, wrapAjs } from "../ajs-response";
import { buildParams, PAGINATION_SEARCH_FIELDS } from "../http-params.util";

/**
 * DiscountsApiService (Angular 18).
 *
 * Replaces apps/b2b-ui/common/services/api/discountsApi/discountsApi.service.js.
 *
 * `list(version, props)` takes an explicit version string ('v1' or 'v2');
 * defaults to v1. All other endpoints use v1.
 */
const DISCOUNTS_BASE_V1 = "/v1/api/discounts";
const DISCOUNTS_BASE_V2 = "/v2/api/discounts";

interface DiscountPayload {
  id?: number | string;
  name: string;
  code: string;
  values: unknown;
  description?: string;
}

@Injectable({ providedIn: "root" })
export class DiscountsApiService {
  private readonly http = inject(HttpClient);

  list(
    version?: "v1" | "v2",
    props?: Record<string, unknown>,
  ): Promise<AjsResponse<unknown>> {
    const base = version === "v2" ? DISCOUNTS_BASE_V2 : DISCOUNTS_BASE_V1;
    return wrapAjs(
      this.http.get<unknown>(base, {
        params: buildParams(props, PAGINATION_SEARCH_FIELDS),
      }),
    );
  }

  detail(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${DISCOUNTS_BASE_V1}/${id}`));
  }

  create(props: DiscountPayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(DISCOUNTS_BASE_V1, {
        name: props.name,
        code: props.code,
        values: props.values,
        description: props.description,
      }),
    );
  }

  update(props: DiscountPayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(`${DISCOUNTS_BASE_V1}/${props.id}`, {
        id: props.id,
        name: props.name,
        code: props.code,
        values: props.values,
        description: props.description,
      }),
    );
  }

  preview(props: {
    discountId: number | string;
    priceListId: number | string;
  }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(
        `${DISCOUNTS_BASE_V1}/${props.discountId}/price/list/${props.priceListId}`,
      ),
    );
  }

  remove(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${DISCOUNTS_BASE_V1}/${id}`));
  }
}
